import { useEffect, useLayoutEffect, useRef, useState, Fragment, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Users, User as UserIcon, Shield, Plus, Sliders, Contact, Building2, Wrench, Truck, Trash2, ChevronDown, Settings, Loader2, Workflow, Database, FileText, PanelLeft, Upload, KeyRound, type LucideIcon } from 'lucide-react';
import { api, apiFetch, useAuth } from '@onvice/portal-shell-api';

import { getModuleLabel, getModalPortalTarget } from '@onvice/portal-ui';

// 2026-07-17: Portal-Tab. Bringt Mandant-weite Settings (Bundesland, Startseite,
// Sidebar-Default, Maskottchen) ins Adressbuch — vorher unter
// /uebersicht/verwaltung?tab=tenant, das war fuer Admins schwer zu finden.
const TenantSettingsAdminPage = lazy(() => import('../uebersicht/TenantSettingsAdminPage'));

// 2026-07-18: Workflows-Tab. Iframe-Embed der Workflow-Engine — Editor +
// Executions + Logs. Sichtbar nur fuer Admin/GF oder Mitglieder der
// Gruppe "Developer" (siehe useAuth().isInGroup).
const WorkflowEditorEmbed = lazy(() => import('../../components/WorkflowEditorEmbed'));

// 2026-07-18: DB-Viewer (Phase 1, Admin-only). Access-artiges Grid ueber
// alle Tenant-DB-Tabellen mit Filter/Sort/Pagination/Detail. Read-only.
const DbViewerPage = lazy(() => import('./DbViewerPage'));

// 2026-07-18: ACL-Simulator (Admin-only). Was sieht User X in Modul Y —
// Gruppen, Modul-, Feld-, Doc-Zugriff mit Quellen (direct/group/default).
const AclSimulatorPage = lazy(() => import('./AclSimulatorPage'));
// 2026-08-17: Sidebar-Label-Editor (Tenant-Alias + Sichtbarkeit pro Slug).
const AdminSidebarLabelsPage = lazy(() => import('./AdminSidebarLabelsPage'));
const NfcTerminalKeyPanel = lazy(() => import('./NfcTerminalKeyPanel'));

type Level = 'admin' | 'editor' | 'author' | 'tester' | 'reader' | 'depositor' | 'none';

interface ModuleStat {
  module_slug: string;
  person_count: number;
  group_count: number;
  default_level: Level;
}

const LEVEL_COLOR: Record<Level, string> = {
  admin:    'bg-brand-500/20 text-brand-300 border-brand-500/30',
  editor:   'bg-sky-500/20 text-sky-300 border-sky-500/30',
  author:   'bg-blue-500/20 text-blue-300 border-blue-500/30',
  tester:   'bg-purple-500/20 text-purple-300 border-purple-500/30',
  reader:   'bg-slate-500/20 text-slate-300 border-slate-500/30',
  depositor:'bg-amber-500/20 text-amber-300 border-amber-500/30',
  none:     'bg-slate-800 text-slate-500 border-slate-700',
};

interface Person {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  mobile: string | null;
  weekly_hours: number;
  employed_since: string | null;
  active: boolean;
  user_role: string;
  groups: { id: string; name: string }[];
}

interface Group {
  id: string;
  name: string;
  description: string | null;
  person_count: number;
  subgroup_count: number;
}

type Tab = 'persons' | 'contacts' | 'groups' | 'modules' | 'sections' | 'portal' | 'workflows' | 'database' | 'aclsim' | 'sidebar' | 'nfc';

type ContactKategorie = 'kunde' | 'ansprechpartner' | 'lieferant' | 'externer_handwerker';

interface Contact {
  id: string;
  typus: 'firma' | 'person';
  firma_id: string | null;
  firma_name: string | null;
  kuerzel: string | null;
  anrede: string | null;
  titel: string | null;
  vorname: string | null;
  nachname: string | null;
  position: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  telefon: string | null;
  email: string | null;
  kategorien: ContactKategorie[];
  active: boolean;
}

interface Props {
  /** Erstauswahl des Tabs. Default 'persons'. */
  initialTab?: Tab;
  /** Eingebettet (UebersichtAdressbuch nutzt eigene Sub-Tab-Bar) → interne
   *  Tab-Bar + Header ausblenden. Tab bleibt auf initialTab fixiert. */
  hideTabBar?: boolean;
}

// 2026-09-15: aclsim/sidebar/nfc ergaenzt — Deep-Links wie ?tab=nfc landeten
// vorher stillschweigend auf 'persons'.
const VALID_TABS: Tab[] = ['persons', 'contacts', 'groups', 'modules', 'sections', 'portal', 'workflows', 'database', 'aclsim', 'sidebar', 'nfc'];

// 2026-09-15 (Joerg): 12 Tab-Eintraege sprengten die Leiste (Container max-w-5xl) —
// die rechten Eintraege waren abgeschnitten. Selten genutzte Verwaltungs-Tabs
// liegen jetzt in Aufklapp-Hauptpunkten (Rechte/Portal/System). Dropdown via
// createPortal in den Modal-Target, sonst Clipping durch Vorfahren; Click-Outside
// ueber data-popover-Attribut auf Trigger UND Panel (Portal ist kein DOM-Child).
interface AdminTabMenuItem {
  /** Interner Tab — gesetzt bei Tab-Eintraegen. */
  tab?: Tab;
  /** Externe Route — gesetzt bei Navigations-Eintraegen (DMS-Status). */
  navigateTo?: string;
  label: string;
  icon: LucideIcon;
  title?: string;
}

function AdminTabDropdown({ label, items, activeTab, onSelectTab }: {
  label: string;
  items: AdminTabMenuItem[];
  activeTab: Tab;
  onSelectTab: (t: Tab) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const popKey = `admin-tabmenu-${label}`;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest(`[data-popover="${popKey}"]`)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, popKey]);

  const active = items.find((i) => i.tab !== undefined && i.tab === activeTab);

  return (
    <div data-popover={popKey}>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${
          active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
        }`}
      >
        {active ? `${label}: ${active.label}` : label}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          data-popover={popKey}
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: 224, zIndex: 60 }}
          className="rounded-lg border border-default bg-card shadow-xl py-1"
        >
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = item.tab !== undefined && item.tab === activeTab;
            return (
              <button
                key={item.label}
                title={item.title}
                onClick={() => {
                  setOpen(false);
                  if (item.navigateTo) navigate(item.navigateTo);
                  else if (item.tab) onSelectTab(item.tab);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                  isActive ? 'bg-primary/15 text-primary font-medium' : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon size={14} className={isActive ? undefined : 'text-muted-foreground'} />
                {item.label}
              </button>
            );
          })}
        </div>,
        getModalPortalTarget(),
      )}
    </div>
  );
}

export default function AdminAdressbuchPage({ initialTab, hideTabBar }: Props = {}) {
  const navigate = useNavigate();
  const { isAdmin, isInGroup } = useAuth();
  const canAccessWorkflows = isAdmin || isInGroup('Developer');
  const [rootSearchParams] = useSearchParams();
  // 2026-07-15: Query-Param ?tab=contacts (aus KundenStammPage-Redirect + „+ Neuer
  // Kontakt"-Dropdown) wurde vorher ignoriert — der Default blieb 'persons'.
  const urlTab = rootSearchParams.get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(
    (urlTab && VALID_TABS.includes(urlTab) ? urlTab : null) ?? initialTab ?? 'persons'
  );
  const [persons, setPersons] = useState<Person[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [modules, setModules] = useState<ModuleStat[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newGroupBusy, setNewGroupBusy] = useState(false);
  const [newGroupErr, setNewGroupErr] = useState<string | null>(null);

  const [showNewPerson, setShowNewPerson] = useState(false);
  const [newPersonFirst, setNewPersonFirst] = useState('');
  const [newPersonLast, setNewPersonLast] = useState('');
  const [newPersonEmail, setNewPersonEmail] = useState('');
  const [newPersonPw, setNewPersonPw] = useState('');
  const [newPersonRole, setNewPersonRole] = useState<'handwerker' | 'admin'>('handwerker');
  const [newPersonBusy, setNewPersonBusy] = useState(false);
  const [newPersonErr, setNewPersonErr] = useState<string | null>(null);

  // 2026-09-06 (Joerg, Fahrplan A4 — Rest des Smoke-Altfindings 2026-08-23):
  // Modul-Sichtbarkeit VOR dem Admin-Call pruefen — gleiche Quelle wie die
  // Sidebar (/portal/my-access, Slug 'adressbuch' = Bedingung des Backend-
  // Guards requireModuleAccess). Ohne Pruefung feuerte die fuer alle
  // erreichbare Route (Dashboard-Kachel „Mein Profil") den Admin-Endpoint
  // als Nicht-Berechtigter: 403 + Konsolenfehler + stille Leer-Liste.
  // null = Pruefung laeuft noch.
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (isAdmin) { setAllowed(true); return; }
    api.get<{ success: boolean; data: { departments?: Array<{ modules?: Array<{ slug: string }> }> } }>('/portal/my-access')
      .then((res) => {
        const slugs = new Set<string>();
        for (const dept of res.data?.departments ?? []) {
          for (const mod of dept.modules ?? []) slugs.add(mod.slug);
        }
        setAllowed(slugs.has('adressbuch'));
      })
      .catch(() => setAllowed(false));
  }, [isAdmin]);

  useEffect(() => {
    // Contacts + Sections + Portal + Workflows + Database haben eigenes Data-Loading.
    if (tab === 'contacts' || tab === 'sections' || tab === 'portal' || tab === 'workflows' || tab === 'database' || tab === 'aclsim' || tab === 'nfc') {
      setLoading(false);
      return;
    }
    // Erst laden, wenn die Sichtbarkeits-Pruefung durch ist (A4).
    if (allowed !== true) {
      if (allowed === false) setLoading(false);
      return;
    }
    // groups/modules sind requirePortalAdmin — als Nicht-Admin nicht feuern,
    // auch nicht via Direktaufruf ?tab=groups (Tab-Bar zeigt sie eh nur Admins).
    if ((tab === 'groups' || tab === 'modules') && !isAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const endpoint = tab === 'persons'
      ? '/admin/adressbuch/persons'
      : tab === 'groups'
        ? '/admin/adressbuch/groups'
        : '/admin/adressbuch/modules';
    api.get<{ success: boolean; data: Person[] | Group[] | ModuleStat[] }>(endpoint)
      .then((res) => {
        if (tab === 'persons') setPersons(res.data as Person[]);
        else if (tab === 'groups') setGroups(res.data as Group[]);
        else setModules(res.data as ModuleStat[]);
      })
      .catch(() => {
        if (tab === 'persons') setPersons([]);
        else if (tab === 'groups') setGroups([]);
        else setModules([]);
      })
      .finally(() => setLoading(false));
  }, [tab, allowed, isAdmin]);

  async function createGroup() {
    setNewGroupBusy(true);
    setNewGroupErr(null);
    try {
      const res = await api.post<{ success: boolean; data: { id: string } }>(
        '/admin/adressbuch/groups',
        { name: newGroupName.trim(), description: newGroupDesc.trim() || null },
      );
      setShowNewGroup(false);
      setNewGroupName(''); setNewGroupDesc('');
      navigate(`/admin/adressbuch/groups/${res.data.id}`);
    } catch (err) {
      setNewGroupErr((err as Error).message ?? 'Anlegen fehlgeschlagen');
    } finally {
      setNewGroupBusy(false);
    }
  }

  async function createPerson() {
    setNewPersonBusy(true);
    setNewPersonErr(null);
    try {
      const res = await api.post<{ success: boolean; data: { person_id: string } }>(
        '/admin/adressbuch/persons',
        {
          firstName: newPersonFirst.trim(),
          lastName: newPersonLast.trim(),
          email: newPersonEmail.trim(),
          password: newPersonPw,
          role: newPersonRole,
        },
      );
      setShowNewPerson(false);
      setNewPersonFirst(''); setNewPersonLast(''); setNewPersonEmail(''); setNewPersonPw('');
      setNewPersonRole('handwerker');
      navigate(`/admin/adressbuch/persons/${res.data.person_id}`);
    } catch (err) {
      setNewPersonErr((err as Error).message ?? 'Anlegen fehlgeschlagen');
    } finally {
      setNewPersonBusy(false);
    }
  }

  const q = search.trim().toLowerCase();
  const filteredPersons = q
    ? persons.filter((p) =>
        p.first_name.toLowerCase().includes(q) ||
        p.last_name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        (p.groups ?? []).some((g) => g.name.toLowerCase().includes(q)),
      )
    : persons;
  const filteredGroups = q
    ? groups.filter((g) =>
        g.name.toLowerCase().includes(q) ||
        (g.description ?? '').toLowerCase().includes(q),
      )
    : groups;
  const filteredModules = q
    ? modules.filter((m) =>
        m.module_slug.toLowerCase().includes(q) ||
        getModuleLabel(m.module_slug).toLowerCase().includes(q),
      )
    : modules;

  // Conditional Return erst NACH allen Hooks (React: stabile Hook-Anzahl —
  // Merker aus dem #300-Vorfall 2026-08-28, siehe AdressbuchMitarbeiterListe).
  if (allowed === false) {
    return (
      <div className="px-4 sm:px-8 py-6 max-w-5xl mx-auto">
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-center text-slate-400">
          Für das Adressbuch fehlt dir die Berechtigung. Die Sichtbarkeit wird über die Adressbuch-Gruppen gesteuert — bei Bedarf beim Administrator melden.
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 py-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        {hideTabBar ? <div /> : <h1 className="text-xl font-bold">Adressbuch</h1>}
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {tab === 'persons'
              ? `${filteredPersons.length} / ${persons.length} Mitarbeiter`
              : tab === 'groups'
                ? `${filteredGroups.length} / ${groups.length} Gruppen`
                : tab === 'modules'
                  ? `${filteredModules.length} / ${modules.length} Module`
                  : ''}
          </span>
          {/* SD-2026-00027 (Onvice, 2026-07-31): Aktionsbuttons ausblenden wenn
              Backend-Guard `requirePortalAdmin` sowieso 403 werfen wuerde. */}
          {tab === 'persons' && isAdmin && (
            <button
              onClick={() => navigate('/admin/adressbuch/persons/new')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm"
              title="Neue Person im Adressbuch anlegen"
            >
              <Plus size={14} /> Neue Person
            </button>
          )}
          {tab === 'groups' && isAdmin && (
            <button
              onClick={() => setShowNewGroup(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm"
            >
              <Plus size={14} /> Neue Gruppe
            </button>
          )}
        </div>
      </div>

      {showNewPerson && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !newPersonBusy && setShowNewPerson(false)}>
          <div className="bg-slate-900 border border-slate-800 rounded-xl max-w-md w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-100">Neue Person</h3>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm">
                <span className="text-slate-400 text-[12px]">Vorname</span>
                <input
                  value={newPersonFirst}
                  onChange={(e) => setNewPersonFirst(e.target.value)}
                  autoFocus
                  className="mt-1 w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-slate-400 text-[12px]">Nachname</span>
                <input
                  value={newPersonLast}
                  onChange={(e) => setNewPersonLast(e.target.value)}
                  className="mt-1 w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="text-slate-400 text-[12px]">E-Mail</span>
              <input
                type="email"
                value={newPersonEmail}
                onChange={(e) => setNewPersonEmail(e.target.value)}
                className="mt-1 w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-400 text-[12px]">Kennwort (min. 8 Zeichen)</span>
              <input
                type="text"
                value={newPersonPw}
                onChange={(e) => setNewPersonPw(e.target.value)}
                className="mt-1 w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-400 text-[12px]">Rolle</span>
              <select
                value={newPersonRole}
                onChange={(e) => setNewPersonRole(e.target.value as 'handwerker' | 'admin')}
                className="mt-1 w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
              >
                <option value="handwerker">Standard (Berechtigung über Gruppen)</option>
                <option value="admin">Admin (Bypass auf alle Module)</option>
              </select>
            </label>
            {newPersonErr && <p className="text-[12px] text-red-400">{newPersonErr}</p>}
            {(() => {
              const missing: string[] = [];
              if (!newPersonFirst.trim()) missing.push('Vorname');
              if (!newPersonLast.trim()) missing.push('Nachname');
              if (!newPersonEmail.trim()) missing.push('E-Mail');
              if (newPersonPw.length < 8) missing.push('Kennwort (min. 8 Zeichen)');
              return missing.length > 0 ? (
                <p className="text-[12px] text-amber-400">Noch ausfüllen: {missing.join(', ')}</p>
              ) : null;
            })()}
            <p className="text-[11px] text-slate-500">
              Gruppen-Zuordnung und weitere Berechtigungen werden anschließend auf der Person-Detail-Seite gesetzt.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowNewPerson(false)}
                disabled={newPersonBusy}
                className="px-3 py-2 rounded text-slate-300 hover:bg-slate-800"
              >Abbrechen</button>
              <button
                onClick={createPerson}
                disabled={newPersonBusy || !newPersonFirst.trim() || !newPersonLast.trim() || !newPersonEmail.trim() || newPersonPw.length < 8}
                className="px-3 py-2 rounded bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-primary-foreground"
              >{newPersonBusy ? 'Anlegen…' : 'Anlegen'}</button>
            </div>
          </div>
        </div>
      )}

      {showNewGroup && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !newGroupBusy && setShowNewGroup(false)}>
          <div className="bg-slate-900 border border-slate-800 rounded-xl max-w-md w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-100">Neue Gruppe</h3>
            <label className="block text-sm">
              <span className="text-slate-400 text-[12px]">Name</span>
              <input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                autoFocus
                className="mt-1 w-full h-9 px-2 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-400 text-[12px]">Beschreibung (optional)</span>
              <textarea
                value={newGroupDesc}
                onChange={(e) => setNewGroupDesc(e.target.value)}
                rows={2}
                className="mt-1 w-full px-2 py-1.5 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm"
              />
            </label>
            {newGroupErr && <p className="text-[12px] text-red-400">{newGroupErr}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowNewGroup(false)}
                disabled={newGroupBusy}
                className="px-3 py-2 rounded text-slate-300 hover:bg-slate-800"
              >Abbrechen</button>
              <button
                onClick={createGroup}
                disabled={newGroupBusy || !newGroupName.trim()}
                className="px-3 py-2 rounded bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-primary-foreground"
              >{newGroupBusy ? 'Anlegen…' : 'Anlegen'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {!hideTabBar && (
      <div className="flex flex-wrap gap-1 border-b border-default mb-4">
        <button
          onClick={() => { setTab('persons'); setSearch(''); }}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${
            tab === 'persons'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <UserIcon size={14} /> Mitarbeiter
        </button>
        <button
          onClick={() => { setTab('contacts'); setSearch(''); }}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${
            tab === 'contacts'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Contact size={14} /> Kontakte
        </button>
        <button
          onClick={() => { setTab('groups'); setSearch(''); }}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${
            tab === 'groups'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users size={14} /> Gruppen
        </button>
        {/* Aufklapp-Hauptpunkte (Joerg 2026-09-15): Rechte / Portal / System.
            SD-2026-00028-Sichtbarkeiten bleiben erhalten — Portal-Gruppe nur
            Admin (Save-Endpoint verlangt requirePortalAdmin), Workflows nur
            Admin/GF + Gruppe Developer. */}
        <AdminTabDropdown
          label="Rechte"
          activeTab={tab}
          onSelectTab={(t) => { setTab(t); setSearch(''); }}
          items={[
            { tab: 'modules', label: 'Module', icon: Shield },
            { tab: 'sections', label: 'Abschnitte', icon: Sliders },
            ...(isAdmin ? [{
              tab: 'aclsim' as Tab, label: 'Rechte-Simulator', icon: Shield,
              title: 'Rechte-Simulator: was sieht Person X in Modul Y (nur Admin)',
            }] : []),
          ]}
        />
        {isAdmin && (
          <AdminTabDropdown
            label="Portal"
            activeTab={tab}
            onSelectTab={(t) => { setTab(t); setSearch(''); }}
            items={[
              {
                tab: 'portal', label: 'Portal-Einstellungen', icon: Settings,
                title: 'Mandant-weite Portal-Einstellungen: Bundesland, Startseite, Sidebar-Default, Maskottchen',
              },
              {
                tab: 'sidebar', label: 'Sidebar-Beschriftungen', icon: PanelLeft,
                title: 'Anzeige-Text + Sichtbarkeit pro Sidebar-Eintrag (nur Admin)',
              },
            ]}
          />
        )}
        {(canAccessWorkflows || isAdmin) && (
          <AdminTabDropdown
            label="System"
            activeTab={tab}
            onSelectTab={(t) => { setTab(t); setSearch(''); }}
            items={[
              ...(canAccessWorkflows ? [{
                tab: 'workflows' as Tab, label: 'Workflows', icon: Workflow,
                title: 'Workflow-Editor + Logs (nur Admin/GF und Gruppe Developer)',
              }] : []),
              ...(isAdmin ? [
                {
                  tab: 'database' as Tab, label: 'Datenbank', icon: Database,
                  title: 'Datenbank-Viewer read-only (nur Admin)',
                },
                {
                  navigateTo: '/m/dms/admin/status-konfiguration', label: 'DMS-Status', icon: FileText,
                  title: 'DMS-Status-Konfiguration: Status, Übergänge und Rechte pro Dokument-Typ (öffnet DMS-Modul)',
                },
                {
                  tab: 'nfc' as Tab, label: 'NFC-Terminal', icon: KeyRound,
                  title: 'NFC-Terminal (Loxone Code Touch): Terminal-Schlüssel anzeigen + rotieren (nur Admin)',
                },
              ] : []),
            ]}
          />
        )}
      </div>
      )}

      {/* Suche — nur fuer Listen-Tabs, nicht fuer Portal/Workflows/Database/ACL-Sim/Sidebar (eigene Panels) */}
      {tab !== 'portal' && tab !== 'workflows' && tab !== 'database' && tab !== 'aclsim' && tab !== 'sidebar' && tab !== 'nfc' && (
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === 'persons' ? 'Name, E-Mail oder Gruppe…' : 'Gruppen-Name oder Beschreibung…'}
            className="w-full h-10 pl-10 pr-4 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/50 text-sm"
          />
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-slate-400">Laden…</div>
      ) : tab === 'portal' ? (
        <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
          <TenantSettingsAdminPage />
        </Suspense>
      ) : tab === 'workflows' ? (
        canAccessWorkflows ? (
          <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
            <WorkflowEditorEmbed />
          </Suspense>
        ) : (
          <div className="text-center py-16 text-slate-400">
            Kein Zugriff. Nur Admin/GF oder Mitglieder der Gruppe „Developer" duerfen den Workflow-Editor sehen.
          </div>
        )
      ) : tab === 'database' ? (
        isAdmin ? (
          <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
            <DbViewerPage />
          </Suspense>
        ) : (
          <div className="text-center py-16 text-slate-400">
            Kein Zugriff. Nur Admin darf den Datenbank-Viewer nutzen.
          </div>
        )
      ) : tab === 'aclsim' ? (
        isAdmin ? (
          <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
            <AclSimulatorPage />
          </Suspense>
        ) : (
          <div className="text-center py-16 text-slate-400">
            Kein Zugriff. Nur Admin darf den ACL-Simulator nutzen.
          </div>
        )
      ) : tab === 'sidebar' ? (
        isAdmin ? (
          <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
            <AdminSidebarLabelsPage />
          </Suspense>
        ) : (
          <div className="text-center py-16 text-slate-400">
            Kein Zugriff. Nur Admin darf Sidebar-Labels bearbeiten.
          </div>
        )
      ) : tab === 'nfc' ? (
        isAdmin ? (
          <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
            <NfcTerminalKeyPanel />
          </Suspense>
        ) : (
          <div className="text-center py-16 text-slate-400">
            Kein Zugriff. Nur Admin darf den NFC-Terminal-Schlüssel verwalten.
          </div>
        )
      ) : tab === 'persons' ? (
        <PersonsList
          persons={filteredPersons}
          onDelete={async (p) => {
            if (!window.confirm(`Person "${p.first_name} ${p.last_name}" (${p.email}) endgueltig loeschen?\n\nLogin-Account, Gruppen-Mitgliedschaften und alle ACL-Eintraege werden mitgeloescht. Aufgaben/Berichte bleiben erhalten, der Owner wird auf null gesetzt.`)) return;
            try {
              await api.delete(`/admin/adressbuch/persons/${p.id}`);
              // Persons-Liste neu laden
              const endpoint = tab === 'persons' ? '/admin/adressbuch/persons' : null;
              if (endpoint) {
                const r = await api.get<{ success: boolean; data: Person[] }>(endpoint);
                setPersons(r.data);
              }
            } catch (err) {
              window.alert(`Loeschen fehlgeschlagen: ${(err as Error).message}`);
            }
          }}
        />
      ) : tab === 'contacts' ? (
        <ContactsTab search={search} />
      ) : tab === 'groups' ? (
        <GroupsList groups={filteredGroups} />
      ) : tab === 'modules' ? (
        <ModulesList modules={filteredModules} />
      ) : (
        <FieldAccessMatrix />
      )}
    </div>
  );
}

// ============================ Kontakte-Reiter (Welle 4, 2026-07-06) ============================
// Externe Kontakte: Kunden, Ansprechpartner, Lieferanten, Ext. Handwerker.
// Nachbau des Adressbuch-Kontakte-Mockups: Kategorie-Chips + Firmen/Personen-
// Toggle + gemischte Liste + Klick auf Firma → Kundenakte falls Kategorie=kunde.

const KATEGORIE_LABEL: Record<ContactKategorie, string> = {
  kunde: 'Kunde',
  ansprechpartner: 'Ansprechpartner',
  lieferant: 'Lieferant',
  externer_handwerker: 'Ext. Handwerker',
};

const KATEGORIE_ICON: Record<ContactKategorie, typeof Building2> = {
  kunde: Building2,
  ansprechpartner: UserIcon,
  lieferant: Truck,
  externer_handwerker: Wrench,
};

const KATEGORIE_COLOR: Record<ContactKategorie, string> = {
  kunde: 'bg-brand-500/15 text-brand-300 border-brand-500/30',
  ansprechpartner: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  lieferant: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  externer_handwerker: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
};

function ContactsTab({ search }: { search: string }) {
  const { isAdmin, hasPermission } = useAuth();
  // SD-2026-00021 v3 (Onvice, 2026-08-01): Permission-basiert (adressbuch.write kommt
  // ueber MODULE_PERMISSION_MAPPING aus der Adressbuch-Gruppe des Users). Rollen-Fallback
  // (admin) bleibt, damit DB-Admin ohne Gruppen den Button behaelt.
  const canCreate = isAdmin || hasPermission('adressbuch.write');
  const canDelete = canCreate; // gleiche Permission wie Create/Edit
  const [items, setItems] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kategorie, setKategorie] = useState<ContactKategorie | 'alle'>('alle');
  const [typus, setTypus] = useState<'alle' | 'firma' | 'person'>('alle');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Kunden-CSV-Import (Joerg 2026-08-30): Notes-Export als Kunden anlegen.
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState<{
    created: number; total: number;
    ansprechpartner?: number;
    skipped: Array<{ rowNumber: number; name: string; reason: string }>;
    errors: Array<{ rowNumber: number; name: string; error: string }>;
  } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  async function runImport(file: File): Promise<void> {
    setImportBusy(true); setImportError(null); setImportResult(null);
    try {
      const r = await apiFetch<{ success: boolean; data: { created: number; total: number; ansprechpartner?: number; skipped: Array<{ rowNumber: number; name: string; reason: string }>; errors: Array<{ rowNumber: number; name: string; error: string }> } }>(
        '/adressbuch/contacts/import-csv',
        { method: 'POST', headers: { 'Content-Type': 'text/csv' }, body: file },
      );
      setImportResult(r.data);
      // Liste neu laden (gleiche Filter)
      const params = new URLSearchParams();
      if (kategorie !== 'alle') params.set('kategorie', kategorie);
      if (typus !== 'alle') params.set('typus', typus);
      if (search) params.set('q', search);
      const list = await api.get<{ success: boolean; data: Contact[] }>(`/adressbuch/contacts?${params.toString()}`);
      setItems(list.data ?? []);
    } catch (err) {
      setImportError((err as Error).message);
    } finally {
      setImportBusy(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  }

  // Loesch-Modal-State (Joerg 2026-08-08): Muelleimer im Kunden-Adressbuch.
  const [deleting, setDeleting] = useState<Contact | null>(null);
  const [deleteBlockers, setDeleteBlockers] = useState<Array<{ label: string; count: number }> | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function openDelete(contact: Contact, e: React.MouseEvent): Promise<void> {
    e.preventDefault(); e.stopPropagation();
    setDeleting(contact);
    setDeleteBlockers(null);
    setDeleteError(null);
    try {
      const r = await api.get<{ success: boolean; data: { deletable: boolean; blockers: Array<{ label: string; count: number }> } }>(`/adressbuch/contacts/${contact.id}/blocking-refs`);
      setDeleteBlockers(r.data?.blockers ?? []);
    } catch (err) {
      setDeleteError((err as Error).message);
      setDeleteBlockers([]);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.delete(`/adressbuch/contacts/${deleting.id}?mode=hard`);
      setItems((prev) => prev.filter((it) => it.id !== deleting.id));
      setDeleting(null);
    } catch (err) {
      const e = err as Error & { payload?: { blockers?: Array<{ label: string; count: number }> } };
      if (e.payload?.blockers) setDeleteBlockers(e.payload.blockers);
      setDeleteError(e.message ?? 'Loeschen fehlgeschlagen');
    } finally {
      setDeleteBusy(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (kategorie !== 'alle') params.set('kategorie', kategorie);
    if (typus !== 'alle') params.set('typus', typus);
    if (search) params.set('q', search);
    api.get<{ success: boolean; data: Contact[] }>(`/adressbuch/contacts?${params.toString()}`)
      .then((r) => setItems(r.data ?? []))
      .catch((e: unknown) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [kategorie, typus, search]);

  // W6 (2026-07-06): Deep-Link ?contactId=<x> aus Legacy /kunden/:id-Redirect
  // wird jetzt auf die Detail-Route umgeleitet.
  useEffect(() => {
    const wanted = searchParams.get('contactId');
    if (wanted) navigate(`/admin/adressbuch/contacts/${wanted}`, { replace: true });
  }, [searchParams, navigate]);

  const chips: Array<{ key: ContactKategorie | 'alle'; label: string; count: number }> = [
    { key: 'alle', label: 'Alle', count: items.length },
    ...(Object.keys(KATEGORIE_LABEL) as ContactKategorie[]).map((k) => ({
      key: k,
      label: KATEGORIE_LABEL[k],
      count: items.filter((it) => it.kategorien.includes(k)).length,
    })),
  ];

  return (
    <div className="space-y-3">
      {/* Header mit Neu-Dropdown */}
      {canCreate && (
        <div className="flex items-center justify-end gap-2">
          <input
            ref={importInputRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void runImport(f); }}
          />
          <button
            onClick={() => importInputRef.current?.click()} disabled={importBusy}
            title="Kunden aus Notes-Export importieren (Semikolon-CSV: Anrede;Vorname;Name;…;Kundennummer)"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-700 hover:bg-slate-800 text-slate-100 text-xs disabled:opacity-50"
          >
            {importBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Kunden importieren (CSV)
          </button>
          <details className="relative">
            <summary className="cursor-pointer list-none inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-500 text-primary-foreground text-xs">
              <Plus size={12} /> Neuer Kontakt <ChevronDown size={12} />
            </summary>
            <div className="absolute right-0 mt-1 w-56 rounded-lg border border-slate-700 bg-slate-900 shadow-lg z-10 py-1">
              <Link to="/admin/adressbuch/contacts/new?typus=firma&kategorie=kunde"
                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-800 text-sm text-slate-100">
                <Building2 size={14} className="text-brand-400" /> Kunde (Firma)
              </Link>
              <Link to="/admin/adressbuch/contacts/new?typus=person&kategorie=kunde"
                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-800 text-sm text-slate-100">
                <UserIcon size={14} className="text-brand-400" /> Kunde (Einzelperson)
              </Link>
              <Link to="/admin/adressbuch/contacts/new?typus=person&kategorie=ansprechpartner"
                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-800 text-sm text-slate-100">
                <UserIcon size={14} className="text-sky-400" /> Ansprechpartner
              </Link>
              <Link to="/admin/adressbuch/contacts/new?typus=firma&kategorie=lieferant"
                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-800 text-sm text-slate-100">
                <Truck size={14} className="text-amber-400" /> Lieferant
              </Link>
              <Link to="/admin/adressbuch/contacts/new?typus=person&kategorie=externer_handwerker"
                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-800 text-sm text-slate-100">
                <Wrench size={14} className="text-purple-400" /> Externer Handwerker
              </Link>
            </div>
          </details>
        </div>
      )}
      {/* Kategorie-Chips */}
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => setKategorie(c.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
              kategorie === c.key
                ? 'bg-brand-500/20 text-brand-200 border-brand-500/50'
                : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
            }`}
          >
            {c.label} <span className="opacity-60">({c.count})</span>
          </button>
        ))}
      </div>

      {/* Typus-Toggle */}
      <div className="inline-flex rounded-md border border-slate-800 bg-slate-900/50 p-0.5">
        {(['alle', 'firma', 'person'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTypus(t)}
            className={`px-3 py-1 rounded text-xs transition-colors ${
              typus === t ? 'bg-brand-600 text-primary-foreground' : 'text-slate-400 hover:bg-slate-800'
            }`}
          >
            {t === 'alle' ? 'Alle' : t === 'firma' ? '🏢 Firmen' : '👤 Personen'}
          </button>
        ))}
      </div>

      {/* Import-Ergebnis / -Fehler (Joerg 2026-08-30) */}
      {(importResult || importError) && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             onClick={() => { setImportResult(null); setImportError(null); }}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full p-5 space-y-3 max-h-[80vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-100">Kunden-Import</h3>
            {importError && (
              <div className="text-rose-300 bg-rose-500/15 border border-rose-500/30 px-3 py-2 rounded text-sm">{importError}</div>
            )}
            {importResult && (
              <>
                <p className="text-sm text-slate-200">
                  <span className="font-medium text-emerald-300">{importResult.created}</span> von {importResult.total} Kunden angelegt
                  {(importResult.ansprechpartner ?? 0) > 0 && <> · <span className="text-sky-300">{importResult.ansprechpartner} Ansprechpartner verknüpft</span></>}
                  {importResult.skipped.length > 0 && <> · <span className="text-amber-300">{importResult.skipped.length} übersprungen</span></>}
                  {importResult.errors.length > 0 && <> · <span className="text-rose-300">{importResult.errors.length} Fehler</span></>}
                </p>
                {importResult.skipped.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-amber-300 mb-1">Übersprungen (Duplikate)</div>
                    <ul className="text-xs text-slate-300 space-y-0.5">
                      {importResult.skipped.map((sk) => (
                        <li key={sk.rowNumber}>Zeile {sk.rowNumber} — {sk.name}: {sk.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {importResult.errors.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-rose-300 mb-1">Fehler</div>
                    <ul className="text-xs text-slate-300 space-y-0.5">
                      {importResult.errors.map((er) => (
                        <li key={er.rowNumber}>Zeile {er.rowNumber} — {er.name}: {er.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-end">
              <button onClick={() => { setImportResult(null); setImportError(null); }}
                      className="px-4 py-2 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm">
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loesch-Modal (Joerg 2026-08-08) */}
      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => deleteBusy ? null : setDeleting(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-semibold text-slate-100 mb-1">Kontakt loeschen</h3>
              <p className="text-sm text-slate-300">
                <span className="font-medium">{deleting.typus === 'firma' ? deleting.firma_name : `${deleting.vorname ?? ''} ${deleting.nachname ?? ''}`.trim()}</span>
                {' '}wirklich unwiderruflich loeschen?
              </p>
            </div>
            {deleteBlockers === null ? (
              <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Pruefe Verknuepfungen…</div>
            ) : deleteBlockers.length > 0 ? (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
                <div className="text-sm text-amber-200 font-medium">Loeschen nicht moeglich — der Kunde ist noch verknuepft:</div>
                <ul className="text-xs text-amber-100 space-y-0.5">
                  {deleteBlockers.map((b) => (
                    <li key={b.label}>· {b.count} {b.label}</li>
                  ))}
                </ul>
                <div className="text-[11px] text-amber-300/80 italic">Erst wenn alle verknuepften Datensaetze entfernt sind, kann der Kunde geloescht werden.</div>
              </div>
            ) : (
              <div className="rounded-md border border-slate-700 bg-slate-800/50 p-3 text-xs text-slate-300">
                Keine Verknuepfungen gefunden — Loeschen ist moeglich.
              </div>
            )}
            {deleteError && <div className="text-xs text-rose-400">{deleteError}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleting(null)} disabled={deleteBusy} className="px-3 py-2 text-sm text-slate-300 hover:text-slate-100 disabled:opacity-50">
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => { void confirmDelete(); }}
                disabled={deleteBusy || deleteBlockers === null || deleteBlockers.length > 0}
                className="px-4 py-2 text-sm rounded-md bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-40 flex items-center gap-1.5"
              >
                {deleteBusy && <Loader2 size={14} className="animate-spin" />}
                Endgueltig loeschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {error && <div className="text-sm text-rose-400">{error}</div>}
      {loading ? (
        <div className="text-center py-8 text-slate-400">Laden…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-slate-400">Keine Kontakte gefunden.</div>
      ) : (
        <div className="rounded-lg border border-slate-800 divide-y divide-slate-800 overflow-hidden">
          {items.map((it) => {
            const displayName = it.typus === 'firma'
              ? it.firma_name
              : `${it.anrede ? it.anrede + ' ' : ''}${it.titel ? it.titel + ' ' : ''}${it.vorname ?? ''} ${it.nachname ?? ''}`.trim();
            return (
              <div key={it.id} className="w-full flex items-stretch hover:bg-slate-800/40">
                <Link
                  to={`/admin/adressbuch/contacts/${it.id}`}
                  className="flex-1 min-w-0 flex items-center gap-3 px-4 py-2.5"
                >
                  <div className="flex-shrink-0 text-slate-400 text-lg">
                    {it.typus === 'firma' ? '🏢' : '👤'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-100 truncate">{displayName}</div>
                    <div className="text-xs text-slate-400 truncate">
                      {it.typus === 'firma' && it.ort && <span>{it.plz ? it.plz + ' ' : ''}{it.ort}</span>}
                      {it.typus === 'person' && it.position && <span>{it.position}</span>}
                      {it.email && <span className="ml-2">· {it.email}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 flex-shrink-0">
                    {it.kategorien.map((k) => {
                      const Icon = KATEGORIE_ICON[k];
                      return (
                        <span
                          key={k}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${KATEGORIE_COLOR[k]}`}
                        >
                          <Icon size={10} /> {KATEGORIE_LABEL[k]}
                        </span>
                      );
                    })}
                  </div>
                </Link>
                {canDelete && (
                  <button
                    type="button"
                    onClick={(e) => { void openDelete(it, e); }}
                    className="px-3 flex items-center justify-center text-rose-400 hover:text-rose-300 hover:bg-rose-900/20 border-l border-slate-800 min-w-[44px]"
                    title="Kontakt loeschen"
                    aria-label="Kontakt loeschen"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ModulesList({ modules }: { modules: ModuleStat[] }) {
  if (modules.length === 0) {
    return <div className="text-center py-8 text-slate-400">Keine Module gefunden</div>;
  }
  return (
    <div className="space-y-2">
      {modules.map((m) => (
        <Link
          key={m.module_slug}
          to={`/admin/adressbuch/modules/${m.module_slug}`}
          className="block px-4 py-3 rounded-lg border border-default bg-card hover:border-primary/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-brand-400" />
            <span className="text-sm font-medium text-foreground">{getModuleLabel(m.module_slug)}</span>
            <span className="font-mono text-[11px] text-muted-foreground">{m.module_slug}</span>
            <span className="text-[11px] text-muted-foreground ml-auto">
              {m.person_count} {m.person_count === 1 ? 'Person' : 'Personen'} · {m.group_count} {m.group_count === 1 ? 'Gruppe' : 'Gruppen'}
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-400">
            <span>Default:</span>
            <span className={`px-2 py-0.5 rounded-full border ${LEVEL_COLOR[m.default_level]}`}>
              {m.default_level}
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function PersonsList({ persons, onDelete }: { persons: Person[]; onDelete: (p: Person) => void | Promise<void> }) {
  if (persons.length === 0) {
    return <div className="text-center py-8 text-slate-400">Keine Mitarbeiter gefunden</div>;
  }
  return (
    <div className="space-y-2">
      {persons.map((p) => (
        <div
          key={p.id}
          className={`flex items-stretch gap-1 rounded-lg border hover:border-primary/50 transition-colors ${p.active ? 'border-default bg-card' : 'border-default bg-card opacity-60'}`}
        >
          <Link
            to={`/admin/adressbuch/persons/${p.id}`}
            className="flex items-center gap-3 px-4 py-3 flex-1 min-w-0 rounded-l-lg"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{p.first_name} {p.last_name}</span>
                {!p.active && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">inaktiv</span>}
                <span className="text-[11px] text-muted-foreground">{p.email}</span>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {(p.groups ?? []).length === 0 ? (
                  <span className="text-[11px] text-slate-500 italic">keine Gruppen</span>
                ) : (
                  (p.groups ?? []).map((g) => (
                    <span key={g.id} className="text-[11px] px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-300 border border-brand-500/20">
                      {g.name}
                    </span>
                  ))
                )}
                {p.user_role === 'admin' && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/20">
                    Admin (Bypass)
                  </span>
                )}
              </div>
            </div>
            <div className="text-right text-[11px] text-muted-foreground hidden sm:block">
              <div>{p.weekly_hours} h / Woche</div>
              {p.mobile && <div>{p.mobile}</div>}
            </div>
          </Link>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); void onDelete(p); }}
            className="px-3 rounded-r-lg text-rose-400 hover:bg-rose-500/10 border-l border-default flex items-center justify-center min-w-[44px]"
            title="Person loeschen"
            aria-label={`Person ${p.first_name} ${p.last_name} loeschen`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

function GroupsList({ groups }: { groups: Group[] }) {
  if (groups.length === 0) {
    return <div className="text-center py-8 text-slate-400">Keine Gruppen gefunden</div>;
  }
  return (
    <div className="space-y-2">
      {groups.map((g) => (
        <Link
          key={g.id}
          to={`/admin/adressbuch/groups/${g.id}`}
          className="block px-4 py-3 rounded-lg border border-default bg-card hover:border-primary/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Users size={16} className="text-brand-400" />
            <span className="font-medium">{g.name}</span>
            <span className="text-[11px] text-muted-foreground ml-auto">
              {g.person_count} {g.person_count === 1 ? 'Person' : 'Personen'}
              {g.subgroup_count > 0 && ` + ${g.subgroup_count} Untergruppen`}
            </span>
          </div>
          {g.description && (
            <p className="text-xs text-muted-foreground mt-1">{g.description}</p>
          )}
        </Link>
      ))}
    </div>
  );
}

// ====================== Field-Access-Matrix (Welle 2026-06-14) ======================

type FALevel = 'none' | 'read' | 'write';
interface FAEntry { slug: string; subject_type: 'person' | 'group' | 'default'; subject_id: string | null; level: FALevel; subject_name: string | null; }

const FA_LABEL: Record<string, string> = {
  'addressbook.tab.stammdaten':        'Tab Stammdaten',
  'addressbook.tab.zugriffe':          'Tab Zugriffe',
  'addressbook.tab.sicherheit':        'Tab Sicherheit',
  'addressbook.tab.akte':              'Tab Akte',
  'addressbook.section.personalien':   'Sektion Personalien',
  'addressbook.section.anschrift':     'Sektion Anschrift',
  'addressbook.section.notfallkontakt':'Sektion Notfallkontakt',
  'addressbook.section.beschaeftigung':'Sektion Beschäftigung',
  'addressbook.section.arbeitszeit':   'Sektion Arbeitszeit',
  'addressbook.section.konditionen':   'Sektion Konditionen',
};

function FieldAccessMatrix() {
  const [entries, setEntries] = useState<FAEntry[]>([]);
  const [slugs, setSlugs] = useState<string[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get<{ success: boolean; data: { entries: FAEntry[]; slugs: string[] } }>('/admin/adressbuch/field-access'),
      api.get<{ success: boolean; data: Group[] }>('/admin/adressbuch/groups'),
    ]).then(([fa, gr]) => {
      setEntries(fa.data.entries ?? []);
      setSlugs(fa.data.slugs ?? []);
      setGroups(gr.data ?? []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setLevel = async (slug: string, subjectType: 'person' | 'group' | 'default', subjectId: string | null, level: FALevel): Promise<void> => {
    const key = `${slug}|${subjectType}|${subjectId ?? ''}`;
    setBusy(key);
    setMsg(null);
    try {
      await api.put('/admin/adressbuch/field-access', { slug, subjectType, subjectId, level });
      load();
    } catch (e) {
      setMsg(`Fehler: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Laden…</div>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Pro Sektion und Tab der Mitarbeiterakte: welche Gruppe darf lesen oder bearbeiten. <strong>Default</strong> gilt fuer alle ohne expliziten Eintrag.
        Wer keine Rechte fuer einen Tab hat, sieht ihn gar nicht; wer keine Rechte fuer eine Sektion hat, sieht eine „Keine Rechte"-Karte.
        Admin (System-Rolle) hat ueberall implizit <code>write</code>.
      </p>
      {msg && <div className="px-3 py-2 rounded-lg text-sm bg-rose-500/10 text-rose-300">{msg}</div>}
      {slugs.map((slug) => {
        const defaultEntry = entries.find((e) => e.slug === slug && e.subject_type === 'default');
        const groupEntries = entries.filter((e) => e.slug === slug && e.subject_type === 'group');
        const usedGroupIds = new Set(groupEntries.map((e) => e.subject_id));
        const availableGroups = groups.filter((g) => !usedGroupIds.has(g.id));
        return (
          <div key={slug} className="rounded-lg border border-default bg-card p-4 space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-foreground">{FA_LABEL[slug] ?? slug}</h3>
              <span className="font-mono text-[11px] text-muted-foreground">{slug}</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm">
              <div className="text-slate-300 self-center">Default (alle ohne expliziten Eintrag)</div>
              <select className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm"
                      disabled={busy !== null}
                      value={defaultEntry?.level ?? 'none'}
                      onChange={(e) => setLevel(slug, 'default', null, e.target.value as FALevel)}>
                <option value="none">none</option>
                <option value="read">read</option>
                <option value="write">write</option>
              </select>
              {groupEntries.map((e) => (
                <Fragment key={e.subject_id ?? ''}>
                  <div className="text-slate-200 self-center">
                    <Users size={12} className="inline mr-1 text-brand-400" />{e.subject_name}
                  </div>
                  <select className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm"
                          disabled={busy !== null}
                          value={e.level}
                          onChange={(ev) => setLevel(slug, 'group', e.subject_id, ev.target.value as FALevel)}>
                    <option value="none">entfernen</option>
                    <option value="read">read</option>
                    <option value="write">write</option>
                  </select>
                </Fragment>
              ))}
              {availableGroups.length > 0 && (
                <>
                  <div className="text-slate-500 italic self-center">Gruppe hinzufuegen…</div>
                  <select className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm"
                          disabled={busy !== null}
                          value=""
                          onChange={(ev) => { const gid = ev.target.value; if (gid) setLevel(slug, 'group', gid, 'read'); }}>
                    <option value="">+ Gruppe…</option>
                    {availableGroups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
